import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { learnApi } from './api';
import { SubscriptionModal } from '@/components/SubscriptionModal';
import { useTutorialStore } from './stores/tutorialStore';

export default function LearnPage() {
  const { t } = useTranslation();
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { getLastTutorial } = useTutorialStore();
  const [hoveredCourse, setHoveredCourse] = useState<string | null>(null);
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!authLoading && !session?.user?.id) {
      console.log('LearnPage - Session not initialized, retrying in 1s...');
      const timer = setTimeout(() => {
        window.location.reload();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [session, authLoading]);
  
  const {
    data: learningPaths,
    isLoading: isLoadingPaths,
    error: pathsError,
  } = useQuery({
    queryKey: ['learningPaths'],
    queryFn: () => learnApi.getLearningPaths(),
  });
  
  // Auto-expand first learning path on initial load
  useEffect(() => {
    if (learningPaths?.length && Object.keys(expandedPaths).length === 0) {
      // Only set the first path to true, all others should be false
      const initialExpanded = {};
      learningPaths.forEach(path => {
        initialExpanded[String(path.id)] = path.id === learningPaths[0].id;
      });
      setExpandedPaths(initialExpanded);
    }
  }, [learningPaths, expandedPaths]);

  if (authLoading) {
    console.log('LearnPage - Still loading auth...');
    return (
      <div className="flex items-center justify-center min-h-screen pt-16 bg-base-300">
        <div className="relative">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <div className="absolute inset-0 animate-pulse-glow opacity-70"></div>
        </div>
      </div>
    );
  }
  if (!session?.user?.id) {
    console.log('LearnPage - No user session found, waiting for initialization...');
    return (
      <div className="flex items-center justify-center min-h-screen pt-16 bg-base-300">
        <div className="relative">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <div className="absolute inset-0 animate-pulse-glow opacity-70"></div>
        </div>
      </div>
    );
  }
  console.log('LearnPage - Auth loaded, session state:', !!session);

  const { data: progress } = useQuery({
    queryKey: ['progress', session.user.id],
    queryFn: async () => await learnApi.getUserProgress(session.user.id),
    enabled: !!session.user.id,
  });

  const { data: userProfile } = useQuery({
    queryKey: ['userProfile', session.user.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .single();
      return data;
    },
    enabled: !!session.user.id,
  });

  const [showSubscriptionModal, setShowSubscriptionModal] = React.useState(false);
  const isLoading = isLoadingPaths;
  const error = pathsError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-16 bg-base-300">
        <div className="relative">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <div className="absolute inset-0 animate-pulse-glow opacity-70"></div>
        </div>
      </div>
    );
  }
  if (error) {
    console.error('Error loading learning paths:', error);
    return (
      <div className="flex flex-col items-center justify-center min-h-screen pt-16 bg-base-300">
        <div className="text-error mb-4 font-tech">{t('learn.errors.loadingFailed')}</div>
        <button className="btn btn-primary neon-glow font-tech" onClick={() => window.location.reload()}>
          {t('common.retry')}
        </button>
      </div>
    );
  }
  if (!learningPaths?.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen pt-16 bg-base-300">
        <div className="text-base-content/70 mb-4 font-tech">{t('learn.noContent')}</div>
      </div>
    );
  }

  // Sequential course access check.
  const canAccessCourse = (courseId: number, courses: any[]) => {
    const sortedCourses = [...courses].sort((a, b) => a.id - b.id);
    const courseIndex = sortedCourses.findIndex((c) => c.id === courseId);
    if (courseIndex === -1) return false;
    if (courseIndex === 0) return true;
    const previousCourse = sortedCourses[courseIndex - 1];
    const completedCount = previousCourse.tutorials.filter(
      (tut: any) => progress && progress[Number(tut.id)]
    ).length;
    return completedCount === previousCourse.tutorials.length;
  };

  const handleCourseClick = async (path: any, course: any) => {
    if (!session?.user?.id) {
      navigate('/login');
      return;
    }
    if (!canAccessCourse(course.id, path.courses)) {
      toast.error(
        t('learn.completePreviousCourse') ||
          'Please complete the previous course before accessing this one.'
      );
      return;
    }
    const courseProgress = await learnApi.getCourseProgress(
      path.slug,
      course.slug,
      session.user.id
    );
    const availableTutorials = await learnApi.getAvailableTutorials(
      path.slug,
      course.slug,
      session.user.id
    );
    const nextTutorial = availableTutorials.find((tut: any) => !tut.isCompleted);
    if (nextTutorial) {
      navigate(`/learn/${path.slug}/${course.slug}/${nextTutorial.id}`);
    } else {
      navigate(`/learn/${path.slug}/${course.slug}/${course.tutorials[0].id}`);
    }
  };

  const handleUpgrade = () => {
    setShowSubscriptionModal(false);
    navigate('/subscribe');
  };

  return (
    <div className="min-h-screen flex flex-col bg-base-300 home-page overflow-x-hidden relative">
      {/* Background circuit effect */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-5 pointer-events-none"></div>
      
      {/* Animated circuit lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="circuit-line w-full h-1 bg-primary/20 absolute top-[10%] left-0 animate-pulse"></div>
        <div className="circuit-line w-1 h-full bg-secondary/20 absolute top-0 left-[20%] animate-pulse" style={{animationDelay: '0.7s'}}></div>
        <div className="circuit-line w-full h-1 bg-accent/20 absolute top-[90%] left-0 animate-pulse" style={{animationDelay: '1.2s'}}></div>
        <div className="circuit-line w-1 h-full bg-primary/20 absolute top-0 left-[80%] animate-pulse" style={{animationDelay: '1.7s'}}></div>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 pt-16 sm:pt-20 relative z-10">
          {/* Page Header */}
          <div className="mb-10">
            <div className="glitch-container inline-block mb-2">
              <h1 
                data-text={t('learn.title')}
                className="text-3xl sm:text-5xl font-bold text-primary font-tech tracking-wider"
              >
                {t('learn.title')}
              </h1>
            </div>
            <div className="h-1 w-20 bg-primary mt-3"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left side: Learning Paths and Courses */}
            <div className="lg:col-span-2">
              <div className="flex flex-col space-y-4">
                {learningPaths.map((path: any, pathIndex: number) => {
                  // Calculate total progress for this learning path
                  const totalTutorials = path.courses.reduce((acc: number, course: any) => 
                    acc + course.tutorials.length, 0);
                  const completedTutorials = path.courses.reduce((acc: number, course: any) => {
                    return acc + course.tutorials.filter(
                      (tut: any) => progress && progress[Number(tut.id)]
                    ).length;
                  }, 0);
                  const pathProgressPercentage = totalTutorials > 0
                    ? Math.round((completedTutorials / totalTutorials) * 100)
                    : 0;
                  
                  const pathId = String(path.id);
                  const isExpanded = expandedPaths[pathId] || false;
                  const toggleExpanded = () => {
                    // Fix to ensure only one path expands at a time
                    setExpandedPaths(prev => {
                      const newState = {};
                      // Set all paths to false
                      Object.keys(prev).forEach(key => {
                        newState[key] = false;
                      });
                      // Toggle just this path
                      newState[pathId] = !prev[pathId];
                      return newState;
                    });
                  };
                  
                  return (
                    <div 
                      key={`path-${path.id}`} 
                      className="bg-base-200/80 backdrop-blur-sm rounded-lg overflow-hidden border border-primary/20 shadow-lg"
                    >
                      {/* Learning Path Header - Always visible */}
                      <div 
                        className="p-4 border-b border-primary/20 bg-base-300/50 cursor-pointer transition-all hover:bg-base-300/70"
                        onClick={toggleExpanded}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-4">
                              <span className="text-primary font-bold font-tech">{pathIndex + 1}</span>
                            </div>
                            <div>
                              <h2 className="text-xl font-bold font-tech-secondary neon-text">{path.title}</h2>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="w-24 bg-base-300/50 rounded-full h-2 overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-500
                                      ${pathProgressPercentage >= 100 
                                        ? 'bg-success' 
                                        : 'bg-primary/70'
                                      }`}
                                    style={{ width: `${pathProgressPercentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs whitespace-nowrap font-mono text-base-content/90">
                                  {completedTutorials}/{totalTutorials}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Expand/Collapse Arrow */}
                          <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              className="h-5 w-5 text-primary"
                              viewBox="0 0 20 20" 
                              fill="currentColor"
                            >
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      {/* Courses - Expandable content */}
                      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 grid gap-2">
                          {path.courses
                            .slice()
                            .sort((a, b) => a.id - b.id)
                            .map((course: any, index: number) => {
                              const courseKey = `${path.slug}-${course.slug}`;
                              const completedCount = course.tutorials.filter(
                                (tut: any) => progress && progress[Number(tut.id)]
                              ).length;
                              const totalTutorials = course.tutorials.length;
                              const progressPercentage =
                                totalTutorials > 0
                                  ? Math.round((completedCount / totalTutorials) * 100)
                                  : 0;
                              const isAccessible = canAccessCourse(course.id, path.courses);
                              const isHovered = hoveredCourse === courseKey;
                              
                              return (
                                <div
                                  key={`course-${courseKey}`}
                                  onClick={() => isAccessible && handleCourseClick(path, course)}
                                  onMouseEnter={() => setHoveredCourse(courseKey)}
                                  onMouseLeave={() => setHoveredCourse(null)}
                                  className={`
                                    relative flex items-center justify-between p-3 rounded-lg
                                    transition-all duration-300 transform
                                    ${isAccessible
                                      ? 'bg-base-100/70 hover:bg-base-100 hover:shadow-md cursor-pointer hover:translate-x-1'
                                      : 'bg-base-300/50 cursor-not-allowed'}
                                    border-l-4 ${isAccessible 
                                      ? progressPercentage >= 100 
                                        ? 'border-l-success' 
                                        : 'border-l-primary' 
                                      : 'border-l-base-content/20'}
                                    ${isHovered && isAccessible ? 'shadow-[0_0_10px_rgba(255,0,128,0.2)]' : ''}
                                    overflow-hidden
                                  `}
                                  title={!isAccessible ? t('learn.completePreviousCourse') : ''}
                                >
                                  {/* Course content */}
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-base-300/50 flex items-center justify-center">
                                      <span className="text-xs font-mono">{index + 1}</span>
                                    </div>
                                    <div>
                                      <h3 className="font-medium text-sm sm:text-base flex items-center gap-1">
                                        {course.title}
                                        {!isAccessible && (
                                          <svg
                                            className="w-3 h-3 text-base-content/70"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                            />
                                          </svg>
                                        )}
                                      </h3>
                                      <div className="text-xs text-base-content/90">
                                        {totalTutorials} {t('learn.tutorials')}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    {course.difficulty && (
                                      <span 
                                        className={`badge badge-${getDifficultyColor(course.difficulty)} 
                                          badge-xs py-2 px-2 font-mono text-[8px] tracking-wider font-bold`}
                                      >
                                        {t(`learn.difficulty.${course.difficulty}`)}
                                      </span>
                                    )}
                                    
                                    <div className="flex items-center gap-1">
                                      <div className="w-16 sm:w-20 bg-base-300/50 rounded-full h-2 overflow-hidden">
                                        <div 
                                          className={`h-full rounded-full transition-all duration-500
                                            ${progressPercentage >= 100 
                                              ? 'bg-success' 
                                              : progressPercentage >= 50
                                                ? 'bg-primary' 
                                                : 'bg-primary/70'
                                            }`}
                                          style={{ width: `${progressPercentage}%` }}
                                        ></div>
                                      </div>
                                      <span className="text-xs whitespace-nowrap font-mono text-base-content/90">
                                        {completedCount}/{totalTutorials}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Right side: Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Subscription info */}
              <div className="bg-base-200/80 backdrop-blur-sm p-6 rounded-lg border border-primary/20 shadow-lg relative overflow-hidden">
                {/* Circuit pattern background */}
                <div className="absolute inset-0 bg-circuit-pattern opacity-5"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center mb-4 gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold font-tech-secondary">{t('learn.trial.title', 'Premium Learning')}</h3>
                  </div>
                  
                  <p className="mb-6 text-base-content/80">{t('learn.trial.description')}</p>
                  
                  {!userProfile?.is_premium && (
                    <button 
                      onClick={() => navigate('/subscribe')} 
                      className="btn btn-primary w-full shadow-lg neon-glow font-tech"
                    >
                      {t('learn.trial.subscribe')}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Features */}
              <div className="bg-base-200/80 backdrop-blur-sm p-6 rounded-lg border border-primary/20 shadow-lg">
                <div className="flex items-center mb-4 gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold font-tech-secondary">{t('learn.features.title', 'Key Features')}</h3>
                </div>
                
                <ul className="space-y-4 mt-4">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-base-content mb-1">{t('learn.features.webFundamentals')}</h4>
                      <p className="text-sm text-base-content/70">{t('learn.features.webFundamentalsDesc', 'Learn HTML, CSS, and JavaScript from the ground up with interactive lessons.')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-base-content mb-1">{t('learn.features.practicalProjects')}</h4>
                      <p className="text-sm text-base-content/70">{t('learn.features.practicalProjectsDesc', 'Build real projects that strengthen your skills and boost your portfolio.')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-base-content mb-1">{t('learn.features.flexibleLearning')}</h4>
                      <p className="text-sm text-base-content/70">{t('learn.features.flexibleLearningDesc', 'Learn at your own pace with our adaptive learning system.')}</p>
                    </div>
                  </li>
                </ul>
              </div>
              
              {/* FAQ Accordion */}
              <div className="bg-base-200/80 backdrop-blur-sm rounded-lg border border-primary/20 shadow-lg overflow-hidden">
                <div className="p-4 border-b border-primary/20 bg-base-300/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold font-tech-secondary">{t('learn.faq.title', 'Frequently Asked Questions')}</h3>
                  </div>
                </div>
                
                <div className="divide-y divide-base-content/10">
                  <div className="collapse">
                    <input type="checkbox" />
                    <div className="collapse-title font-medium">
                      {t('learn.faq.pathTitle')}
                    </div>
                    <div className="collapse-content">
                      <p className="text-base-content/70">{t('learn.faq.pathDescription')}</p>
                    </div>
                  </div>
                  
                  <div className="collapse">
                    <input type="checkbox" />
                    <div className="collapse-title font-medium">
                      {t('learn.faq.skipTitle')}
                    </div>
                    <div className="collapse-content">
                      <p className="text-base-content/70">{t('learn.faq.skipDescription')}</p>
                    </div>
                  </div>
                  
                  <div className="collapse">
                    <input type="checkbox" />
                    <div className="collapse-title font-medium">
                      {t('learn.faq.coursesTitle')}
                    </div>
                    <div className="collapse-content">
                      <p className="text-base-content/70">{t('learn.faq.coursesDescription')}</p>
                    </div>
                  </div>
                  
                  <div className="collapse">
                    <input type="checkbox" />
                    <div className="collapse-title font-medium">
                      {t('learn.faq.focusTitle')}
                    </div>
                    <div className="collapse-content">
                      <p className="text-base-content/70">{t('learn.faq.focusDescription')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Community Support */}
              <div className="bg-base-200/80 backdrop-blur-sm p-6 rounded-lg border border-primary/20 shadow-lg">
                <div className="flex items-center mb-4 gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold font-tech-secondary">{t('learn.support.title')}</h3>
                </div>
                
                <p className="text-base-content/80 mb-6">{t('learn.support.description')}</p>
                
                <button 
                  onClick={() => navigate('/community')} 
                  className="btn btn-outline btn-primary w-full gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 127.14 96.36" fill="currentColor">
                    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                  </svg>
                  {t('learn.support.joinDiscord')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
}

function getDifficultyColor(difficulty: string | undefined): string {
  switch (difficulty) {
    case 'beginner':
      return 'success';
    case 'intermediate':
      return 'warning';
    case 'advanced':
      return 'error';
    default:
      return 'info';
  }
}

function getTruncatedText(text: string, maxLength = 100): string {
  if (!text) return '';
  return text.length <= maxLength ? text : text.substring(0, maxLength) + '...';
}